// Thin fetch wrapper. The session lives in an HttpOnly cookie; nothing sensitive is kept in JS storage.
export class ApiError extends Error {
  status: number;
  issues?: { path: string; message: string }[];
  constructor(status: number, message: string, issues?: { path: string; message: string }[]) {
    super(message);
    this.status = status;
    this.issues = issues;
  }
}

let onUnauthorized: (() => void) | null = null;
export const setUnauthorizedHandler = (fn: () => void) => { onUnauthorized = fn; };

export async function api<T = unknown>(method: string, url: string, body?: unknown): Promise<T> {
  const form = body instanceof FormData;   // file uploads: the browser sets the multipart boundary
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined && !form ? { 'content-type': 'application/json' } : {},
    body: form ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
  if (!res.ok) {
    if (res.status === 401 && !url.startsWith('/api/auth/')) onUnauthorized?.();
    const issues = data?.issues as ApiError['issues'];
    const detail = issues?.length ? `: ${issues.map((i) => `${i.path} ${i.message}`).join('; ')}` : '';
    throw new ApiError(res.status, (data?.error ?? res.statusText) + detail, issues);
  }
  return data as T;
}

export const get = <T>(url: string) => api<T>('GET', url);
export const post = <T>(url: string, body: unknown = {}) => api<T>('POST', url, body);
export const patch = <T>(url: string, body: unknown = {}) => api<T>('PATCH', url, body);

export type Role = 'technician' | 'physician' | 'ngf_admin' | 'department_contact' | 'platform_admin';
export interface Me {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  credential: string | null;
  next: 'change_password' | 'enroll_mfa' | 'verify_mfa' | 'done';
}

export const ROLE_LABEL: Record<Role, string> = {
  technician: 'Technician',
  physician: 'Physician',
  ngf_admin: 'NGF admin',
  department_contact: 'Organization contact',
  platform_admin: 'Platform admin',
};

export const isStaff = (r: Role) => r !== 'department_contact';
export const isAdmin = (r: Role) => r === 'ngf_admin' || r === 'platform_admin';
// Technicians also release, see drug screens, use Imports and add / rename organizations (2026-09-29; the API enforces).
export const canRelease = (r: Role) => r !== 'department_contact';
export const canSeeRestricted = (r: Role) => r !== 'department_contact';
export const canImport = (r: Role) => r === 'technician' || isAdmin(r);
export const canEditOrgs = (r: Role) => r === 'technician' || isAdmin(r);
